<template>
  <div>
    <div
      class="row mt-2 mx-0"
      v-for="(attribute, index) in attributesList"
      :key="index"
    >
      <div
        class="hermes-attribute-key"
        :class="{
          'col-2': states.showAttributeValues,
          'col-6': !states.showAttributeValues,
        }"
      >
        <b-input-group>
          <template #prepend>
            <b-button
              variant="danger"
              @click="deleteAttribute(index)"
              :disabled="
                disableForm ||
                attribute.disableUpdate ||
                attribute.disableDelete
              "
              ><em class="fa fa-times"></em
            ></b-button>
          </template>
          <b-form-input
            v-model="attribute.name"
            placeholder="Enter name"
            @input="updateAttributeName($event, index)"
            :disabled="disableForm || attribute.disableUpdate"
          />
        </b-input-group>
        <b-alert variant="danger" class="mt-2" :show="attribute.isDuplicate">
          <em class="fa fa-times-circle mr-2"></em
          >{{ $t("commons.duplicate_name") }}
        </b-alert>
        <b-alert variant="danger" class="mt-2" :show="attribute.hasNameError">
          <em class="fa fa-times-circle mr-2"></em
          >{{ $t("commons.attribute_mandatory") }}
        </b-alert>
      </div>
      <div
        class="hermes-attribute-key"
        :class="{
          'col-2': states.showAttributeValues,
          'col-6': !states.showAttributeValues,
          'hermes-attribute-border': !states.showAttributeValues,
        }"
      >
        <b-form-select
          v-model="attribute.dataType"
          @input="updateAttributeDataType($event, index)"
          :disabled="disableForm || attribute.disableUpdate"
        >
          <b-form-select-option
            v-for="type in dataTypes"
            :key="type.type"
            v-text="type.type"
            :value="type.type"
            :disabled="type.disabled"
          />
        </b-form-select>
      </div>
      <div
        class="col-8 hermes-attribute-value"
        v-if="states.showAttributeValues"
      >
        <div v-if="attribute.dataType == 'String'">
          <b-form-input
            v-model="attribute.value"
            placeholder="Enter a value"
            @input="updateAttributeValue($event, index)"
            :disabled="disableForm"
          />
          <b-button
            class="mt-1"
            variant="primary"
            v-if="states.allowHTMLEditor"
            v-text="$t('buttons.edit_html')"
            @click="editAsHtml(attribute, index)"
          ></b-button>
          <b-button
            class="mt-1"
            variant="primary"
            v-if="states.allowBase64EncDec"
            v-text="$t('buttons.enc_dec')"
            @click="encDecString(attribute, index)"
          ></b-button>
        </div>
        <div v-if="attribute.dataType == 'Text'">
          <b-form-textarea
            v-model="attribute.value"
            class="textarea"
            maxlines="1"
            @input="updateAttributeValue($event, index)"
            placeholder="Enter script... (content should be in base64 encoded format)"
            :disabled="disableForm"
          ></b-form-textarea>
          <b-button
            class="mt-1"
            variant="primary"
            v-if="states.allowBase64EncDec"
            v-text="$t('buttons.enc_dec')"
            @click="encDecString(attribute, index)"
          ></b-button>
        </div>
        <b-form-input
          v-if="attribute.dataType == 'Number'"
          v-model="attribute.value"
          placeholder="Numeric Value..."
          @input="updateAttributeValue($event, index)"
          type="number"
          :disabled="disableForm"
        />
        <div v-if="attribute.dataType == 'Image'">
          <b-form-file
            v-model="attribute.value.file_content"
            :state="Boolean(attribute.value.file_content)"
            @input="updateFileAttributeValue($event, attribute, index)"
            placeholder="Choose a file or drop it here..."
            drop-placeholder="Drop file here..."
            :disabled="disableForm || states.disableUploads"
            accept="image/*"
          ></b-form-file>
          <pre
            class="bg-light mt-2 p-2 border border-success"
            v-if="attribute.value.content"
            >{{ removePropFromObject(attribute.value, "file_content") }}</pre
          >
          <span v-if="attribute.value.content">
            <strong class="mt-2" v-text="$t('commons.url')" />
            <a :href="attribute.value.content" target="_blank" class="my-2">{{
              attribute.value.content
            }}</a>
          </span>
        </div>
        <div v-if="attribute.dataType == 'File'">
          <b-form-file
            v-model="attribute.value.file_content"
            :state="Boolean(attribute.value.file_content)"
            @input="updateFileAttributeValue($event, attribute, index)"
            placeholder="Choose a file or drop it here..."
            drop-placeholder="Drop file here..."
            :disabled="disableForm"
            accept="application/*, text/*"
          ></b-form-file>
          <pre
            class="bg-light mt-2 p-2 border border-success"
            v-if="attribute.value.content"
            >{{ removePropFromObject(attribute.value, "file_content") }}</pre
          >
          <span v-if="attribute.value.content">
            <strong class="mt-2" v-text="$t('commons.url')" />
            <a :href="attribute.value.content" target="_blank" class="my-2">{{
              attribute.value.content
            }}</a>
          </span>
        </div>
        <toggle-button
          v-if="attribute.dataType == 'Boolean'"
          v-model="attribute.value"
          @input="updateAttributeValue($event, index)"
          :labels="{ checked: 'true', unchecked: 'false' }"
          :width="70"
          :font-size="14"
          class="mt-2"
          :disabled="disableForm"
        />
        <v-jsoneditor
          v-if="attribute.dataType == 'Advanced'"
          v-model="attribute.value"
          :options="options"
          :plus="false"
          @input="updateAttributeValue($event, index)"
          @error="onAttributeError($event, index)"
          :disabled="disableForm"
        ></v-jsoneditor>
        <b-alert
          variant="danger"
          class="mt-2"
          :show="
            attribute.hasError &&
            attribute.dataType != 'File' &&
            attribute.dataType != 'Image'
          "
        >
          <em class="fa fa-times-circle mr-2"></em
          >{{ $t("commons.invalid_data") }}
        </b-alert>
        <b-alert
          variant="danger"
          class="mt-2"
          :show="
            states.showErrors &&
            attribute.hasError &&
            (attribute.dataType == 'File' || attribute.dataType == 'Image')
          "
        >
          <em class="fa fa-times-circle mr-2"></em
          >{{ $t("commons.file_not_selected") }}
        </b-alert>
      </div>
    </div>
    <b-alert variant="info" :show="!attributesList.length">
      <em class="fa fa-info-circle mr-2"></em
      >{{ $t("commons.no_attribues_configured") }}
    </b-alert>
    <b-button
      variant="success"
      class="mt-3"
      @click="onAdd"
      :disabled="disableForm"
      ><em class="fa fa-plus"></em
    ></b-button>

    <html-editor-modal ref="htmlModal"></html-editor-modal>
    <enc-dec-modal ref="encDecModal"></enc-dec-modal>
  </div>
</template>

<script>
import { useManageAttributes } from "../../stores/manage-attributes";
export default {
  computed: {
    attributesList() {
      return this.useManageAttributes.getForm.attributesList;
    },
    dataTypes() {
      return this.useManageAttributes.dataTypes;
    },
    states() {
      return this.useManageAttributes.states;
    },
    disableForm() {
      return this.useManageAttributes.disableForm;
    },
  },
  data() {
    return {
      options: { mode: "code", mainMenuBar: false, statusBar: false },
      useManageAttributes: useManageAttributes(),
    };
  },
  methods: {
    onAdd() {
      this.useManageAttributes.addAttribute();
    },
    deleteAttribute(index) {
      this.useManageAttributes.deleteAttribute(index);
    },
    updateFileAttributeValue(event, attribute, index) {
      // event = event == null ? { type: attribute.type, file_content: null } : event;
      if (!event) {
        event = {
          ...attribute.value,
          type: attribute.dataType.toLowerCase(),
          file_content: null,
        };
      } else if (event instanceof File) {
        event = { type: attribute.dataType.toLowerCase(), file_content: event };
      }

      this.useManageAttributes.updateAttributeValue({
        index: index,
        value: event,
      });

      this.useManageAttributes.updateAttributeError({
        index: index,
        error: false,
      });
    },
    updateAttributeValue(event, index) {
      this.useManageAttributes.updateAttributeValue({
        index: index,
        value: event,
      });
    },
    updateAttributeName(event, index) {
      this.useManageAttributes.updateAttributeName({
        index: index,
        name: event,
      });
    },
    updateAttributeDataType(event, index) {
      if (event == "File" || event == "Image") {
        this.useManageAttributes.updateAttributeValue({
          index: index,
          value: { type: event.toLowerCase(), file_content: null },
        });
        // Set the attribute to error until a file is selected.
        this.useManageAttributes.updateAttributeError({
          index: index,
          error: this.states.disableUploads ? false : true,
        });
      } else {
        this.useManageAttributes.updateAttributeDataType({
          index: index,
          dataType: event,
      });
      }
    },
    onAttributeError(event, index) {
      this.useManageAttributes.updateAttributeError({
        index: index,
        error: true,
      });
    },
    removePropFromObject(obj, prop) {
      const { [prop]: _, ...rest } = obj;
      return { ...rest };
    },
    editAsHtml(attribute, index) {
      this.$refs.htmlModal.open(attribute.value).then((r) => {
        if (r) {
          this.updateAttributeValue(r, index);
        }
      });
    },
    encDecString(attribute, index) {
      this.$refs.encDecModal.open(attribute).then((r) => {
        if (r) {
          this.updateAttributeValue(r, index);
        }
      });
    },
  },
};
</script>
